import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

import { FieldMapService } from "@services/ingest/fieldMapService";
import type { Instructions } from "@models/instructions.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";

const svc = new FieldMapService();

// The readme is a fixed markdown string built server-side (uses only h1/h2, paragraphs,
// and inline code). Map each element to MUI so it matches the rest of the page.
const readmeComponents: Components = {
  h1: ({ children }) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0, mb: 1.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography sx={{ fontSize: 14, lineHeight: 1.6, mb: 1.5, color: "#333" }}>{children}</Typography>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{ fontFamily: "monospace", fontSize: 13, bgcolor: "#f6f4fd", px: 0.5, py: 0.25, borderRadius: 0.5 }}
    >
      {children}
    </Box>
  ),
};

// The customer-facing Launch Data File Instructions, rendered in-app from the config
// (MPN-1243). "Export instructions" on the field-map editor routes here; the sample CSV
// downloads as a file.
export function InstructionsPanel({
  definitionId,
  onBack,
}: {
  definitionId: number;
  /** Back affordance in the header. Omitted → no back link (modal hosts close instead). */
  onBack?: () => void;
}) {
  const [data, setData] = useState<Instructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!definitionId) return;
    setLoading(true);
    svc
      .getInstructions(definitionId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load instructions"))
      .finally(() => setLoading(false));
  }, [definitionId]);

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = async (variant: "customer" | "ops", rows: number) => {
    setDownloadError(null);
    const fallbackName = `${data?.file_key || "file"}_${variant === "ops" ? "test" : "sample"}.csv`;
    try {
      await svc.downloadSampleFile(definitionId, variant, rows, fallbackName);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography sx={{ color: "#8c9097" }}>Loading instructions…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {onBack && (
            <>
              <Box
                component="a"
                onClick={onBack}
                sx={{
                  color: "#845adf",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: 14,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 12, mr: 0.5 }} />
                Back to Field Map
              </Box>
              <Typography sx={{ color: "#8c9097", mx: 1 }}>&middot;</Typography>
            </>
          )}
          <Typography variant="h6" fontWeight={600}>
            Launch Data File Instructions
          </Typography>
        </Box>
        {data && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => download("customer", 1)}
              sx={{ textTransform: "none", bgcolor: "#845adf", "&:hover": { bgcolor: "#6e44c8" } }}
            >
              Sample file (for customer)
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => download("ops", 5)}
              title="Internal only — rows are flagged is_test, so uploading this file never affects audiences, conversions, or billing. Do not send to customers."
              sx={{ textTransform: "none", color: "#845adf", borderColor: "#845adf" }}
            >
              Test file (internal)
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {downloadError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {data && (
        <>
          {/* Overview */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {data.definition_name}
            </Typography>
            <Box sx={{ mt: 1, display: "flex", gap: 4, flexWrap: "wrap", fontSize: 14 }}>
              <Box>
                <Typography sx={{ color: "#8c9097", fontSize: 12 }}>Filename pattern</Typography>
                <Typography sx={{ fontFamily: "monospace" }}>{data.filename_pattern}</Typography>
              </Box>
              {data.population_segment_name && (
                <Box>
                  <Typography sx={{ color: "#8c9097", fontSize: 12 }}>Population segment</Typography>
                  <Typography>{data.population_segment_name}</Typography>
                </Box>
              )}
            </Box>
            {!data.file_key && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No file key set yet — set one on the config so the filename pattern resolves.
              </Alert>
            )}
          </Paper>

          {/* Field table, grouped by category */}
          {data.groups.map((group) => (
            <Paper key={group.category} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {group.label}
                </Typography>
              </Box>
              <Typography sx={{ color: "#8c9097", fontSize: 13, mb: 2 }}>{group.tolerance}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Column", "Type", "Required", "Accepted values", "Format", "Description", "Sample"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase" }}
                        >
                          {h}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.columns.map((c) => (
                    <TableRow key={c.source_field}>
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>
                        {c.source_field}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{c.type_label}</TableCell>
                      <TableCell>
                        {c.required ? (
                          <Chip label="required" size="small" sx={{ bgcolor: "#fdeceb", color: "#bc3627", fontSize: 11, height: 20 }} />
                        ) : (
                          <span style={{ color: "#c4c4c4" }}>optional</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {c.accepted_values.length ? c.accepted_values.join(", ") : <span style={{ color: "#c4c4c4" }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {c.format || <span style={{ color: "#c4c4c4" }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#555", maxWidth: 300 }}>
                        {c.description || <span style={{ color: "#c4c4c4" }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontFamily: "monospace" }}>
                        {c.sample || <span style={{ color: "#c4c4c4" }}>—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}

          {/* Sample CSV */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Sample CSV
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: "#f6f4fd",
                borderRadius: 1,
                overflowX: "auto",
                fontSize: 13,
                fontFamily: "monospace",
              }}
            >
              {data.sample_csv || "(no columns yet)"}
            </Box>
          </Paper>

          {/* Readme */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              Readme
            </Typography>
            <ReactMarkdown components={readmeComponents}>{data.readme}</ReactMarkdown>
          </Paper>
        </>
      )}
    </Box>
  );
}

// ---- Route wrapper ----

export default function InstructionsPage() {
  useDocumentTitle("Launch Data File Instructions");
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const definitionId = Number(configId);

  return (
    <InstructionsPanel
      definitionId={definitionId}
      onBack={() => navigate(`/configs/${definitionId}/field-map`)}
    />
  );
}
